/**
 * Outil « Droite à curseurs » (Boîte à outils, 3e → 2nde).
 *
 * f(x) = ax + b avec deux curseurs : a est la pente (l'escalier « +1 en x,
 * +a en y »), b l'ordonnée à l'origine. Au doigt, on lit une image ou un
 * antécédent, valeurs exactes. Défi : retrouver une droite tracée en pointillés.
 */

import { useRef, useState } from 'react';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import {
  Note,
  ParamSlider,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  Segmented,
  ToggleChip,
} from '../../ui/controls';
import { clamp, makeFrame, snap } from '../../ui/frame';
import { formatNumber } from '../../ui/format';
import { FIGURE_BG_CLASS, HaloText, INK, PlotGrid } from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import {
  affine,
  affineText,
  antecedent,
  CHALLENGE_ROUNDS,
  image,
  nature,
  PARAM_MAX,
  PARAM_MIN,
  PARAM_STEP,
  pickTarget,
} from './line';

const SIZE = 320;
const VP = { xMin: -6, xMax: 6, yMin: -6, yMax: 6 };
const frame = makeFrame(VP, SIZE);
const LINE = 'var(--color-primary-500)';
const READ = 'var(--color-accent-500)';
const STAIR = 'var(--color-xp-500)';
const TARGET = 'var(--color-ink-500)';

interface Challenge {
  round: number;
  target: { a: number; b: number };
  found: number;
}

export function LineSlidersTool({
  onSend,
  sendCost,
  rng = Math.random,
}: ToolProps & { rng?: () => number }) {
  const [a, setA] = useState(2);
  const [b, setB] = useState(1);
  const [mode, setMode] = useState<'image' | 'antecedent'>('image');
  const [pick, setPick] = useState<number | null>(null);
  const [stairs, setStairs] = useState(true);
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const drag = useDrag<never>({
    svgRef,
    frame,
    ids: [],
    pointOf: () => ({ x: 0, y: 0 }),
    onDrag: () => {},
    onBackground: (p) => {
      const v = mode === 'image' ? p.x : p.y;
      setPick(clamp(snap(v, 0.5), -6, 6));
    },
  });

  const line = (() => {
    const x1 = VP.xMin;
    const x2 = VP.xMax;
    return {
      x1: frame.tx(x1),
      y1: frame.ty(affine(a, b, x1)),
      x2: frame.tx(x2),
      y2: frame.ty(affine(a, b, x2)),
    };
  })();

  // lecture : le point de la droite désigné par le doigt
  const reading = (() => {
    if (pick === null) return null;
    if (mode === 'image') {
      const y = image(a, b, pick);
      return { x: pick, y: y.value, text: `f(${formatNumber(pick, 2)}) = ${y.plain}`, send: y };
    }
    const x = antecedent(a, b, pick);
    if (!x) {
      return {
        x: null,
        y: pick,
        text:
          pick === b
            ? `Tous les nombres ont pour image ${formatNumber(b, 2)}.`
            : `${formatNumber(pick, 2)} n’a pas d’antécédent : la droite est horizontale.`,
        send: null,
      };
    }
    return {
      x: x.value,
      y: pick,
      text: `${formatNumber(pick, 2)} a pour antécédent ${x.plain}`,
      send: x,
    };
  })();

  const kind = nature(a, b);
  const kindText =
    kind === 'lineaire'
      ? 'Fonction linéaire (b = 0) : la droite passe par l’origine, c’est une situation de proportionnalité.'
      : kind === 'constante'
        ? b === 0
          ? 'Fonction nulle (a = 0 et b = 0) : à la fois constante et linéaire, la droite est l’axe des abscisses.'
          : 'Fonction constante (a = 0) : la droite est horizontale.'
        : `Fonction affine : la pente a = ${formatNumber(a, 2)}, l’ordonnée à l’origine b = ${formatNumber(b, 2)}.`;

  const matched = challenge !== null && challenge.target.a === a && challenge.target.b === b;
  const startChallenge = () => {
    const target = pickTarget(rng);
    setChallenge({ round: 1, target, found: 0 });
    setA(target.a === 1 ? 2 : 1);
    setB(0);
  };
  const nextRound = () => {
    if (!challenge) return;
    setChallenge({
      round: challenge.round + 1,
      target: pickTarget(rng, challenge.target),
      found: challenge.found + 1,
    });
  };

  const stairFrom = { x: 0, y: b };
  const sx0 = frame.tx(stairFrom.x);
  const sy0 = frame.ty(stairFrom.y);
  const sx1 = frame.tx(stairFrom.x + 1);
  const sy1 = frame.ty(stairFrom.y + a);
  const target = challenge?.target;

  return (
    <div className="space-y-4">
      <p className="font-display text-ink-900 text-xl font-bold" data-testid="line-equation">
        f(x) = {affineText(a, b)}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          ariaLabel="Lecture au doigt"
          value={mode}
          options={[
            { value: 'image', label: 'Image' },
            { value: 'antecedent', label: 'Antécédent' },
          ]}
          onChange={(m) => {
            setMode(m);
            setPick(null);
          }}
        />
        <ToggleChip on={stairs} onClick={() => setStairs((v) => !v)}>
          Escalier
        </ToggleChip>
        <ToggleChip
          on={challenge !== null}
          onClick={() => (challenge ? setChallenge(null) : startChallenge())}
        >
          Défi
        </ToggleChip>
      </div>

      {challenge && (
        <section
          aria-live="polite"
          className="bg-xp-50 space-y-2 rounded-2xl p-3"
          data-testid="line-challenge"
        >
          <p className="text-ink-900 text-sm font-semibold">
            Droite {challenge.round}/{CHALLENGE_ROUNDS} · Règle a et b pour retrouver la droite en
            pointillés.
          </p>
          {matched ? (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-success-600 text-sm font-bold">
                Trouvé : f(x) = {affineText(a, b)}
              </p>
              {challenge.round < CHALLENGE_ROUNDS ? (
                <button type="button" onClick={nextRound} className={cn(PRIMARY_BUTTON, 'ml-auto')}>
                  Droite suivante
                </button>
              ) : (
                <>
                  <button type="button" onClick={startChallenge} className={PRIMARY_BUTTON}>
                    Rejouer
                  </button>
                  <button
                    type="button"
                    onClick={() => setChallenge(null)}
                    className={SECONDARY_BUTTON}
                  >
                    Fin du défi
                  </button>
                </>
              )}
            </div>
          ) : (
            <p className="text-ink-600 text-sm">
              Astuce : lis d’abord où elle coupe l’axe des ordonnées (b), puis compte l’escalier
              (a).
            </p>
          )}
        </section>
      )}

      <div className="mx-auto w-full max-w-[26rem]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="img"
          aria-label={`Droite d’équation y = ${affineText(a, b)}. Touche le graphique pour lire ${
            mode === 'image' ? 'une image' : 'un antécédent'
          }.`}
          {...drag.svgProps}
        >
          <PlotGrid frame={frame} step={1} />
          {target && (
            <line
              x1={frame.tx(VP.xMin)}
              y1={frame.ty(affine(target.a, target.b, VP.xMin))}
              x2={frame.tx(VP.xMax)}
              y2={frame.ty(affine(target.a, target.b, VP.xMax))}
              stroke={TARGET}
              strokeWidth={3}
              strokeDasharray="8 6"
            />
          )}
          <line {...line} stroke={LINE} strokeWidth={3} />
          {stairs && a !== 0 && (
            <g data-testid="line-stairs">
              <line x1={sx0} y1={sy0} x2={sx1} y2={sy0} stroke={STAIR} strokeWidth={2.5} />
              <line x1={sx1} y1={sy0} x2={sx1} y2={sy1} stroke={STAIR} strokeWidth={2.5} />
              <HaloText
                x={(sx0 + sx1) / 2}
                y={sy0 + (a > 0 ? 16 : -7)}
                textAnchor="middle"
                fontSize={13}
                fontWeight={700}
                fill={STAIR}
              >
                +1
              </HaloText>
              <HaloText
                x={sx1 + 6}
                y={(sy0 + sy1) / 2 + 4}
                fontSize={13}
                fontWeight={700}
                fill={STAIR}
              >
                {a > 0 ? '+' : '−'}
                {formatNumber(Math.abs(a), 2)}
              </HaloText>
            </g>
          )}
          <circle cx={frame.tx(0)} cy={frame.ty(b)} r={5} fill={LINE} />
          {reading && reading.x !== null && (
            <g>
              <line
                x1={frame.tx(reading.x)}
                y1={frame.ty(0)}
                x2={frame.tx(reading.x)}
                y2={frame.ty(reading.y)}
                stroke={READ}
                strokeWidth={1.5}
                strokeDasharray="4 4"
              />
              <line
                x1={frame.tx(0)}
                y1={frame.ty(reading.y)}
                x2={frame.tx(reading.x)}
                y2={frame.ty(reading.y)}
                stroke={READ}
                strokeWidth={1.5}
                strokeDasharray="4 4"
              />
              <circle cx={frame.tx(reading.x)} cy={frame.ty(reading.y)} r={6} fill={READ} />
            </g>
          )}
          {reading && reading.x === null && (
            <line
              x1={0}
              y1={frame.ty(reading.y)}
              x2={SIZE}
              y2={frame.ty(reading.y)}
              stroke={READ}
              strokeWidth={1.5}
              strokeDasharray="4 4"
            />
          )}
          <text x={8} y={18} fontSize={13} fill={INK} fontWeight={600}>
            y
          </text>
        </svg>
      </div>

      <div className="flex min-h-12 flex-wrap items-center gap-2">
        <p
          className="text-ink-900 min-w-0 flex-1 text-base font-semibold"
          aria-live="polite"
          data-testid="line-reading"
        >
          {reading
            ? reading.text
            : mode === 'image'
              ? 'Touche le graphique : le doigt choisit x, on lit f(x).'
              : 'Touche le graphique : le doigt choisit y, on lit x.'}
        </p>
        {onSend && reading?.send && (
          <SendToSheetButton
            label={reading.send.plain}
            cost={sendCost}
            onClick={() => onSend({ atoms: reading.send!.atoms })}
          />
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <ParamSlider
          name="a (pente)"
          ariaLabel="Valeur de a, la pente"
          value={a}
          min={PARAM_MIN}
          max={PARAM_MAX}
          step={PARAM_STEP}
          format={(v) => formatNumber(v, 2)}
          onChange={setA}
          color={STAIR}
        />
        <ParamSlider
          name="b (ordonnée à l’origine)"
          ariaLabel="Valeur de b, l’ordonnée à l’origine"
          value={b}
          min={PARAM_MIN}
          max={PARAM_MAX}
          step={PARAM_STEP}
          format={(v) => formatNumber(v, 2)}
          onChange={setB}
          color={LINE}
        />
      </div>

      {!challenge && (
        <Note live={false}>
          <MathText>{`$f(x) = ax + b$`}</MathText> · {kindText}
        </Note>
      )}
    </div>
  );
}
